import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import * as Rx from 'rxjs';
import { tap, map} from 'rxjs/operators';
import { AppSettingService } from '../app.service';
import { UserInfoService } from '../user/user-info.service';
import { CacheService } from '../utility/cache.service';

/* InterestAPIService migrated from informAPI.js -v129 */
@Injectable({
	providedIn: 'root'
})
export class NCESAPIService {
	constructor(
		private userInfo: UserInfoService,
		private http: HttpClient,
		private cacheSrv: CacheService
	) { }

	private url = AppSettingService.appConfig.JourneyAPIUrl + "NCES";
	
	getSchools(queryParams: any, limit: number, offset: number): Promise<any> {
		var keyword = '';
		var city = '';
		var zip = '';
		var state = '';

		if (queryParams.keyword !== '') {
			keyword = queryParams.keyword;
		} else {
			if (queryParams.city !== '' && queryParams.state !== '') {
				city = queryParams.city;
				state = queryParams.state;
			}

			if (queryParams.zip !== '') {
				zip = queryParams.zip;
			}
		}

		return this.http.get<any>(this.url + '/WholeSearch?keyword=' + keyword + '&limit=' + limit + '&offset=' + offset + '&zip=' + zip + '&city=' + city + '&state=' + state).toPromise();
	};

	private PARAMS = new HttpParams({
		fromObject: {
			action: 'opensearch',
			format: 'json',
			origin: '*'
		}
	});

	search(queryParams: any, limit: number, offset: number) {

		var keyword = '';
		var city = '';
		var zip = '';
		var state = '';

		if (queryParams.keyword !== '') {
			keyword = queryParams.keyword;
		} else {
			if (queryParams.city !== '' && queryParams.state !== '') {
				city = queryParams.city;
				state = queryParams.state;
			}

			if (queryParams.zip !== '') {
				zip = queryParams.zip;
			}
		}

		if (queryParams.keyword.length <= 2) {
			return Rx.of([]);
		}

		return this.http.get<[any, any[]]>(this.url + '/WholeSearch?keyword=' + keyword + '&limit=' + limit + '&offset=' + offset + '&zip=' + zip + '&city=' + city + '&state=' + state, { params: this.PARAMS.set('search', keyword) }).pipe(
			//convert the single object we get from this request into an array that we can feed into ngbtypeahead (which expects an array per row for dropdown)
			map((response: any) => {
				var dropDownResults = [];
				if (response.CityResults.length > 0) {
					dropDownResults.push({ isCityResultsHeader: true, hasSchoolZipResults: response.SchoolZipResults.length > 0 ? true : false })
					for (var i = 0; i < response.CityResults.length; i++) {
						let resultObj = {
							type: 'CityResults',
							hasSchoolZipResults: response.SchoolZipResults.length > 0 ? true : false,
							City: null as any,
							State: null as any
						};
						resultObj.City = response.CityResults[i].City;
						resultObj.State = response.CityResults[i].State;
						dropDownResults.push(resultObj);
					}
				}

				if (response.SchoolResults.length > 0) {
					dropDownResults.push({ isSchoolResultsHeader: true, hasSchoolZipResults: response.SchoolZipResults.length > 0 ? true : false })
					for (var i = 0; i < response.SchoolResults.length; i++) {
						let resultObj = {
							type: 'SchoolResults',
							hasSchoolZipResults: response.SchoolZipResults.length > 0 ? true : false,
							NCESSCH: null as any,
							Name: null as any,
							City: null as any,
							State: null as any
						};
						resultObj.NCESSCH = response.SchoolResults[i].NCESSCH;
						resultObj.Name = response.SchoolResults[i].Name;
						resultObj.City = response.SchoolResults[i].City;
						resultObj.State = response.SchoolResults[i].State;
						dropDownResults.push(resultObj);
					}
				}

				var isZipSchoolResponse = false;
				if (response.SchoolZipResults.length > 0) {
					isZipSchoolResponse = true;
					dropDownResults.push({ isSchoolZipResultsSchoolHeader: false, isSchoolZipResultsHeader: true, hasSchoolZipResults: response.SchoolZipResults.length > 0 ? true : false, type: 'SchoolZipResults' })
					dropDownResults.push({ isSchoolZipResultsSchoolHeader: true, type: 'SchoolZipResults' })
					for (var i = 0; i < response.SchoolZipResults.length; i++) {
						let resultObj = {
							type: 'SchoolZipResults',
							hasSchoolZipResults: response.SchoolZipResults.length > 0 ? true : false,
							NCESSCH: null as any,
							Name: null as any,
							City: null as any,
							State: null as any
						};
						resultObj.NCESSCH = response.SchoolZipResults[i].NCESSCH;
						resultObj.Name = response.SchoolZipResults[i].Name;
						resultObj.City = response.SchoolZipResults[i].City;
						resultObj.State = response.SchoolZipResults[i].State;
						dropDownResults.push(resultObj);
					}
				}

				if (isZipSchoolResponse) {
					//we don't want to return any type of entries but zip school response entries, because they will render as blank entries in the typeahead dropdown, so get rid of non-zip-school entries
					var dropDownResultsOnlyZipSchoolResults = [];
					for (var i = 0; i < dropDownResults.length; i++) {
						if (dropDownResults[i].type === 'SchoolZipResults') {
							dropDownResultsOnlyZipSchoolResults.push(dropDownResults[i]);
						}
					}
					dropDownResults = dropDownResultsOnlyZipSchoolResults;
				}
				return dropDownResults;
			})
		);
	}

	getNCESSchool(schoolId:string): Rx.Observable<any> {
		if (schoolId === null || schoolId === "") {
			return Rx.throwError(() => { "please provide nces school number" });
		}
		return this.cacheSrv.get(true, this.url + "/" + schoolId);
	}

	getNCESSchoolNearby(schoolId: string, offset: number, limit:number): Rx.Observable<any> {
		if (schoolId === null || schoolId === "") {
			return Rx.throwError(() => { "please provide nces school number" });
		}
		return this.cacheSrv.get(true, this.url + "/" + schoolId + "/Nearby?offset=" + offset.toString() + "&limit=" + limit.toString());
	}

	saveNCESLogo(schoolId:string, logo:string): Rx.Observable<any> {
		if (schoolId === null || schoolId === "") {
			return Rx.throwError(() => { "please provide nces school number" });
		}
		return this.http.post<any>(
			this.url + "/" + schoolId + "/Logo",
			{ 'Image': logo },
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveNCESSplash(schoolId: string, splash: string): Rx.Observable<any> {
		if (schoolId === null || schoolId === "") {
			return Rx.throwError(() => { "please provide nces school number" });
		}
		return this.http.post<any>(
			this.url + "/" + schoolId + "/Splash",
			{ 'Image': splash },
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
}

